import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicForm, FormField } from 'src/components/Forms';
import {
  FollowUpReasons,
  MemberServicesFollowUpInput,
  useMemberServicesFollowUp,
} from 'src/graphql/entities/member-services-followup';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

export interface MemberServicesFollowUpFormValues {
  reason: { id: string; label: string; value: string };
  additionalNotes: string;
}

export const MemberServicesFollowUpModal = () => {
  const { closeModal } = useModalMethods(Modals.MemberServicesFollowUp);
  const { isOpened, modalProps } = useModalState(Modals.MemberServicesFollowUp);
  const theme = useTheme();
  const { postMemberServicesFollowUp } = useMemberServicesFollowUp();

  const { reset } = useForm<MemberServicesFollowUpFormValues>();

  const onSubmit = useCallback(
    async (data: MemberServicesFollowUpFormValues) => {
      if (!modalProps?.addressId) return;

      await postMemberServicesFollowUp({
        ...data,
        addressId: modalProps.addressId,
        conversationId: modalProps.conversationId,
        additionalNotes: data.additionalNotes,
        reason: data.reason.value as unknown as MemberServicesFollowUpInput['reason'],
      });
      reset();
      closeModal();
    },
    [
      closeModal,
      modalProps?.addressId,
      modalProps?.conversationId,
      postMemberServicesFollowUp,
      reset,
    ],
  );

  const handleClose = useCallback(() => {
    reset();
    closeModal();
  }, [closeModal, reset]);

  const formFields: FormField[] = [
    {
      id: 'reason',
      name: 'reason',
      label: 'Reason for follow-up',
      type: 'autocomplete',
      textFieldProps: {
        variant: 'outlined',
        size: 'medium',
      },
      required: true,
      sx: {
        marginTop: theme.spacing(1),
      },
      autocompleteProps: {
        disableClearable: true,
      },
      options: Object.entries(FollowUpReasons).map(([reasonName, reasonValue]) => ({
        id: reasonName,
        value: reasonValue,
        label: reasonName,
      })),
    },
    {
      id: 'additionalNotes',
      name: 'additionalNotes',
      label: 'Additional Notes',
      type: 'textfield',
      sx: {
        marginTop: theme.spacing(1),
      },
      textFieldProps: {
        variant: 'outlined',
        size: 'medium',
        InputProps: {
          multiline: true,
          minRows: 4,
        },
      },
    },
  ];

  return (
    <Dialog open={isOpened} onClose={handleClose} fullWidth>
      <DialogTitle>
        <Typography>Member Services Follow-up</Typography>
      </DialogTitle>
      <DialogContent>
        <DynamicForm fields={formFields} onSubmit={onSubmit}>
          <Button
            autoFocus
            type="submit"
            sx={{
              alignSelf: 'flex-end',
              width: 'fit-content',
            }}
          >
            Submit
          </Button>
        </DynamicForm>
      </DialogContent>
    </Dialog>
  );
};
